import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import type { PushToken } from "@feed/shared";
import { ApiError, api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { CopyIcon } from "../components/icons";
import { Screen } from "../components/Screen";
import { setLanguage, type Language } from "../i18n";
import { useDocumentTitle } from "../lib/title";
import { mcpUrl } from "../lib/token";

interface Created {
  token: string;
  label: string;
}

export function Settings() {
  const { t, i18n } = useTranslation();
  useDocumentTitle(t("settings.title"));
  const { logout } = useAuth();

  const [tokens, setTokens] = useState<PushToken[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [created, setCreated] = useState<Created | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<"token" | "url" | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const dateFormat = new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "short", year: "numeric" });
  const loadTokens = () =>
    api
      .tokens.list()
      .then(({ tokens: found }) => setTokens(found))
      .catch(() => setLoadFailed(true));

  useEffect(() => {
    void loadTokens();
  }, []);

  async function onCreate(event: FormEvent) {
    event.preventDefault();
    if (label.trim() === "") return;
    setError(null);
    try {
      const { token, record } = await api.tokens.create(label.trim());
      setCreated({ token, label: record.label });
      setCreating(false);
      setLabel("");
      void loadTokens();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.code : "generic");
    }
  }

  async function copy(text: string, what: "token" | "url") {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      window.setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard blocked: the value is on screen, selectable.
    }
  }

  async function revoke(id: string) {
    setRevoking(null);
    try {
      await api.tokens.revoke(id);
      void loadTokens();
    } catch {
      setError("generic");
    }
  }

  return (
    <Screen>
      <header className="page-head">
        <div>
          <h1 className="title" tabIndex={-1}>{t("settings.title")}</h1>
          <p className="subtitle">{t("settings.subtitle")}</p>
        </div>
      </header>

      {created && (
        <section className="block block-job token-banner" aria-labelledby="new-token-title">
          <h2 className="block-heading" id="new-token-title">
            {t("settings.newTitle")}
          </h2>
          <p>{t("settings.newBody")}</p>
          <label className="token-field">
            <span className="token-label">{t("settings.tokenLabel")}</span>
            <input className="token-value" readOnly dir="ltr" value={created.token} onFocus={(event) => event.currentTarget.select()} />
          </label>
          <button type="button" className="pill pill-solid pill-wide" onClick={() => void copy(created.token, "token")}>
            <CopyIcon />
            {copied === "token" ? t("settings.copied") : t("settings.copyToken")}
          </button>
          <label className="token-field">
            <span className="token-label">{t("settings.mcpLabel")}</span>
            <input className="token-value" readOnly dir="ltr" value={mcpUrl(window.location.origin, created.token)} onFocus={(event) => event.currentTarget.select()} />
          </label>
          <button type="button" className="pill pill-outline pill-wide" onClick={() => void copy(mcpUrl(window.location.origin, created.token), "url")}>
            <CopyIcon />
            {copied === "url" ? t("settings.copied") : t("settings.copyUrl")}
          </button>
          <button type="button" className="pill pill-outline pill-wide" onClick={() => setCreated(null)}>
            {t("settings.done")}
          </button>
        </section>
      )}

      <section aria-labelledby="tokens-title" className="stack">
        <div className="section-head">
          <h2 className="section-title" id="tokens-title">
            {t("settings.tokens")}
          </h2>
          {!creating && (
            <button type="button" className="pill pill-ink" onClick={() => { setCreating(true); setCreated(null); }}>
              {t("settings.create")}
            </button>
          )}
        </div>

        {creating && (
          <form className="row form-row" onSubmit={onCreate}>
            <label className="field">
              <span className="label">{t("settings.labelField")}</span>
              <input className="input" type="text" dir="auto" maxLength={60} autoFocus placeholder={t("settings.labelHint")} value={label} onChange={(e) => setLabel(e.target.value)} />
            </label>
            <div className="notice-actions">
              <button type="submit" className="pill pill-ink" disabled={label.trim() === ""}>
                {t("settings.createSubmit")}
              </button>
              <button type="button" className="pill pill-quiet" onClick={() => { setCreating(false); setError(null); }}>
                {t("common.cancel")}
              </button>
            </div>
          </form>
        )}
        {error && (
          <p className="error" role="alert">
            {t(`settings.errors.${error}`, { defaultValue: t("errors.save") })}
          </p>
        )}
        {loadFailed && (
          <p className="error" role="alert">
            {t("errors.load")}
          </p>
        )}
        {tokens === null && !loadFailed && <p className="status">{t("loading")}</p>}
        {tokens?.length === 0 && <p className="status">{t("settings.empty")}</p>}

        <ul className="stack rows">
          {tokens?.map((token) => {
            const revoked = token.revoked_at !== null;
            return (
              <li key={token.id} className={revoked ? "row row-revoked" : "row"}>
                <div className="row-text">
                  <div className="row-top">
                    <strong className={revoked ? "token-name struck" : "token-name"} dir="auto">
                      {token.label}
                    </strong>
                    <span className="badge badge-quiet">{t("settings.push")}</span>
                  </div>
                  <span className="row-time">
                    {revoked
                      ? t("settings.revoked", { date: dateFormat.format(new Date(token.revoked_at!)) })
                      : t("settings.created", { date: dateFormat.format(new Date(token.created_at)) })}
                  </span>
                  {revoking === token.id && <span className="row-question">{t("settings.revokeQuestion")}</span>}
                </div>
                {!revoked &&
                  (revoking === token.id ? (
                    <div className="row-buttons">
                      <button type="button" className="pill pill-danger" onClick={() => void revoke(token.id)} autoFocus>
                        {t("settings.revoke")}
                      </button>
                      <button type="button" className="pill pill-quiet" onClick={() => setRevoking(null)}>
                        {t("common.cancel")}
                      </button>
                    </div>
                  ) : (
                    <button type="button" className="pill pill-danger-outline" onClick={() => setRevoking(token.id)} aria-label={`${t("settings.revoke")}: ${token.label}`}>
                      {t("settings.revoke")}
                    </button>
                  ))}
              </li>
            );
          })}
        </ul>
        <p className="note">{t("settings.note")}</p>
      </section>

      <section className="stack" aria-labelledby="prefs-title">
        <h2 className="section-title" id="prefs-title">
          {t("settings.language")}
        </h2>
        <div className="segmented" role="group" aria-label={t("settings.language")}>
          {(["en", "he"] as Language[]).map((language) => (
            <button key={language} type="button" className="segmented-option" aria-pressed={i18n.language === language} onClick={() => void setLanguage(language)}>
              {t(`language.${language}`)}
            </button>
          ))}
        </div>
        <button type="button" className="pill pill-quiet pill-wide" onClick={() => void logout()}>
          {t("settings.logout")}
        </button>
      </section>
    </Screen>
  );
}
